/**
 * Display formatting shared by the buyer-facing listing card and detail page.
 * Every listing field the public API returns is nullable, so each helper has a
 * defined answer for null rather than leaving "undefined" on the page.
 */

/** "US$ 45,000", or an honest "Price on request" when the realtor did not name one. */
export const fmtPrice = (v) => (v == null || v === '' || Number.isNaN(Number(v))
  ? 'Price on request'
  : `US$ ${Number(v).toLocaleString('en-US')}`);

/** "800 m²" — null when the listing carries no size, so callers can omit the line. */
export const fmtSize = (v) => (v == null || v === '' || Number.isNaN(Number(v))
  ? null
  : `${Number(v).toLocaleString('en-US')} m²`);

/** "Borrowdale · Harare", dropping whichever half is missing; '' when both are. */
export const fmtPlace = (loc) => [loc?.suburb, loc?.city].filter(Boolean).join(' · ');

/** "2" or "2.5" — a half bathroom is real, a trailing ".0" is noise. Null when unknown. */
export const fmtBaths = (v) => (v == null || v === '' || Number.isNaN(Number(v))
  ? null
  : (Number(v) % 1 === 0 ? String(Number(v)) : Number(v).toFixed(1)));

/**
 * The one-line summary under a listing title. Houses lead with the rooms a buyer
 * searches on; stands have no rooms, so they lead with the size and borrow up to
 * two features. Missing fields drop out rather than printing "null bed".
 */
export const fmtFacts = (l) => {
  const parts = [];
  const num = (v) => Number(v).toLocaleString('en-US');
  if (l?.listing_type === 'house') {
    if (l.bedrooms != null) parts.push(`${l.bedrooms} bed`);
    if (l.bathrooms != null) parts.push(`${fmtBaths(l.bathrooms)} bath`);
    if (l.floor_area_sqm != null) parts.push(`${num(l.floor_area_sqm)} m²`);
    if (l.stand_size_sqm != null) parts.push(`${num(l.stand_size_sqm)} m² stand`);
  } else {
    if (l?.stand_size_sqm != null) parts.push(`${num(l.stand_size_sqm)} m² stand`);
    (l?.features || []).slice(0, 2).forEach((f) => parts.push(f));
  }
  return parts.join(' · ');
};

/** "$185k" — a map pin has room for about five characters, not "US$ 185,000". */
export const fmtPriceShort = (v) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return 'POA';
  const n = Number(v);
  if (n >= 1000000) return `$${Number((n / 1000000).toFixed(2))}M`;
  if (n >= 1000) return `$${Number((n / 1000).toFixed(1))}k`;
  return `$${n.toLocaleString('en-US')}`;
};
